import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'providers/AuthProvider';
import WamiaLoader from 'components/common/WamiaLoader';

/**
 * Props for the ProtectedRoute component
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Optional minimum loading time in milliseconds
   * @default 300
   */
  minimumLoaderTime?: number;
}

/**
 * ProtectedRoute ensures that only authenticated users can access the wrapped route.
 * If not authenticated, users are redirected to the login page.
 * Shows a loader during authentication checks and respects a minimum loader display time.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  minimumLoaderTime = 300 
}) => {
  const { isAuthenticated, profileLoading } = useAuth();
  const [showLoader, setShowLoader] = useState(true);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);
  
  // Ensure loader displays for the minimum time
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumTimeElapsed(true);
    }, minimumLoaderTime);
    
    return () => clearTimeout(timer);
  }, [minimumLoaderTime]);
  
  // Hide loader when authentication state is determined and minimum time has passed
  useEffect(() => {
    if (minimumTimeElapsed && !profileLoading && isAuthenticated !== null) {
      setShowLoader(false);
    }
  }, [minimumTimeElapsed, profileLoading, isAuthenticated]);

  // Show loader during authentication checks or minimum display time
  if ((profileLoading || isAuthenticated === null || !minimumTimeElapsed) && showLoader) {
    return (
      <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" 
           style={{ 
             zIndex: 99999, 
             backgroundColor: 'white'
           }}>
        <WamiaLoader />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (isAuthenticated === false) {
    return <Navigate to="/auth/login" />;
  }

  // Render children when authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
